// Tax Reports → "WHT Statement" tab: Withholding Tax Detail in the client's
// layout (one row per invoice; Payment / Receipt / Advance Tax 236H sections,
// each with its own total). Rows are built in utils/whtStatement.js.
// Firebase cost: the same one query the Tax Summary runs (posted documents of
// the financial year) + 1 read per older invoice paid in the period that is
// outside that year; customers / vendors come from the device cache.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiPrinter, FiBarChart2, FiSearch } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { collection, getDocs, getDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../theme/colors';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { getVendorsCached, getCustomersCached } from '../../utils/masterDataStore';
import { addPrintBrand } from '../../utils/printBranding';
import { buildWhtRows, whtTotals, missingInvoiceIds, WHT_SECTION_DEFS } from '../../utils/whtStatement';

const dp = () => (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)) ? Number(window.__AMAL_DECIMAL_PLACES__) : 2);
const fmtAmt = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString('en-PK', { minimumFractionDigits: dp(), maximumFractionDigits: dp() });
  return v < 0 ? `(${s})` : s;
};
const fmtRate = (r) => (Number(r) ? `${Number(r).toLocaleString('en-PK', { maximumFractionDigits: 2 })}%` : '-');
const fmtD = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : s;
};
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const COLS = ['Trans. Date', 'Trans. No.', 'Ref. Trans. No.', 'Ref. Date', 'Bill No.', 'Party', 'NTN No.', 'Tax', 'Taxable Amount', 'Rate', 'Withholding Amount'];

const WhtStatement = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();
  const [financialYears, setFinancialYears] = useState([]);
  const [fyId, setFyId] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(todayStr());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [rows, setRows] = useState({ payment: [], receipt: [], advance: [] });

  const advanceLabel = (activeCompany?.whtAddToTotal && activeCompany?.whtLabel) ? activeCompany.whtLabel : 'Advance Tax 236H';
  const sections = useMemo(() => WHT_SECTION_DEFS.map((s) => (s.key === 'advance' ? { ...s, tax: advanceLabel } : s)), [advanceLabel]);

  const loadFYs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snap = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
      const fys = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      setFyId((prev) => prev || activeFinancialYear?.id || fys[0]?.id || '');
    } catch (e) { console.error('Load financial years:', e); }
  }, [activeCompany, activeFinancialYear]);
  useEffect(() => { loadFYs(); }, [loadFYs]);

  const generate = async () => {
    if (!activeCompany?.id || !fyId) { alert('Please select a financial year'); return; }
    setLoading(true);
    setGenerated(true);
    try {
      const cid = activeCompany.id;
      const snap = await getDocs(query(collection(db, 'companies', cid, 'transactions'),
        where('status', 'in', ['Posted', 'Partial', 'Paid']), where('financialYearId', '==', fyId)));
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const invoices = {};
      all.forEach((t) => { invoices[t.id] = t; });
      const inBranch = (t) => !activeBranch || activeBranch.id === 'all' || t.branchId === activeBranch.id;
      const inPeriod = all.filter((t) => inBranch(t) && (!dateFrom || (t.docDate || '') >= dateFrom) && (!dateTo || (t.docDate || '') <= dateTo));
      // Invoices paid in this period but booked in an earlier year.
      const missing = missingInvoiceIds(inPeriod, invoices);
      const extra = await Promise.all(missing.map((id) => getDoc(doc(db, 'companies', cid, 'transactions', id)).catch(() => null)));
      extra.forEach((s) => { if (s && s.exists()) invoices[s.id] = { id: s.id, ...s.data() }; });
      const [vendList, custList] = await Promise.all([getVendorsCached(cid).catch(() => []), getCustomersCached(cid).catch(() => [])]);
      const toMap = (list) => (list || []).reduce((m, p) => { m[p.id] = p; return m; }, {});
      setRows(buildWhtRows(inPeriod, { invoices, vendors: toMap(vendList), customers: toMap(custList), advanceLabel }));
    } catch (e) {
      console.error('WHT statement:', e);
      alert('Could not load the WHT statement: ' + (e?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const q = search.trim().toLowerCase();
  const shown = useMemo(() => {
    const f = (list) => (!q ? list : list.filter((r) => [r.party, r.transNo, r.refNo, r.billNo, r.ntn].some((v) => String(v || '').toLowerCase().includes(q))));
    return { payment: f(rows.payment), receipt: f(rows.receipt), advance: f(rows.advance) };
  }, [rows, q]);
  const totals = useMemo(() => ({ payment: whtTotals(shown.payment), receipt: whtTotals(shown.receipt), advance: whtTotals(shown.advance) }), [shown]);
  const hasRows = shown.payment.length + shown.receipt.length + shown.advance.length > 0;
  const branchName = activeBranch && activeBranch.id !== 'all' ? activeBranch.branchName : '';
  const periodText = `${fmtD(dateFrom)} - ${fmtD(dateTo)}`;

  // ── Excel: one sheet, the three sections one under the other ──
  const handleExcel = () => {
    const aoa = [[activeCompany?.companyName || ''], ['Withholding Tax Detail'], [periodText + (branchName ? ` · ${branchName}` : '')], []];
    sections.forEach((s) => {
      aoa.push([s.title]);
      aoa.push(COLS);
      shown[s.key].forEach((r) => aoa.push([fmtD(r.date), r.transNo, r.refNo, fmtD(r.refDate), r.billNo, r.party, r.ntn, r.tax, r.taxable, r.rate ? r.rate / 100 : '', r.wht]));
      aoa.push(['', '', '', '', '', '', '', 'Total', totals[s.key].taxable, '', totals[s.key].wht]);
      aoa.push([]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [12, 12, 14, 12, 12, 34, 16, 24, 16, 8, 18].map((w) => ({ wch: w }));
    Object.keys(ws).forEach((k) => {
      if (k[0] === '!') return;
      const col = k.replace(/\d+/g, '');
      if (col === 'J' && typeof ws[k].v === 'number') ws[k].z = '0.00%';
      if ((col === 'I' || col === 'K') && typeof ws[k].v === 'number') ws[k].z = '#,##0.00';
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WHT Statement');
    XLSX.writeFile(wb, `WHT_Statement_${dateFrom}_${dateTo}.xlsx`);
  };

  // ── Print: the client's layout ──
  const handlePrint = () => {
    const c = activeCompany || {};
    const head = `<tr>${COLS.map((h, i) => `<th${i >= 8 ? ' class="r"' : ''}>${h}</th>`).join('')}</tr>`;
    const body = sections.map((s) => {
      const list = shown[s.key];
      const tr = list.length ? list.map((r) => `<tr><td>${fmtD(r.date)}</td><td>${esc(r.transNo)}</td><td>${esc(r.refNo)}</td><td>${fmtD(r.refDate)}</td><td>${esc(r.billNo)}</td><td>${esc(r.party)}</td><td>${esc(r.ntn)}</td><td>${esc(r.tax)}</td><td class="r">${fmtAmt(r.taxable)}</td><td class="r">${fmtRate(r.rate)}</td><td class="r">${fmtAmt(r.wht)}</td></tr>`).join('')
        : `<tr><td colspan="11" class="none">No entries in this period</td></tr>`;
      return `<h3>${esc(s.title)}</h3><table><thead>${head}</thead><tbody>${tr}</tbody>
        <tfoot style="display:table-row-group"><tr class="tot"><td colspan="8" class="r">Total</td><td class="r">${fmtAmt(totals[s.key].taxable)}</td><td></td><td class="r">${fmtAmt(totals[s.key].wht)}</td></tr></tfoot></table>`;
    }).join('');
    const addr = [c.address, c.city].filter(Boolean).join(', ');
    const contact = [c.phone ? `Phone: ${esc(c.phone)}` : '', c.email ? `Email: ${esc(c.email)}` : ''].filter(Boolean).join(' &nbsp; ');
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print.'); return; }
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Withholding Tax Detail</title><style>
      body{font-family:'Segoe UI',Arial,sans-serif;margin:24px;color:#1A1A1A;font-size:11px}
      .co{text-align:center;margin-bottom:12px} .co h1{margin:0;font-size:18px} .co div{font-size:11px;color:#444}
      .co h2{margin:8px 0 2px;font-size:15px} .co .per{font-size:12px}
      h3{font-size:12px;margin:16px 0 4px;color:#800020;border-bottom:1px solid #800020;padding-bottom:2px}
      table{width:100%;border-collapse:collapse;font-size:11px} th{background:#f2f2f2;text-align:left;padding:5px;border:1px solid #ccc;font-size:10px}
      td{padding:4px 5px;border:1px solid #ddd;vertical-align:top} .r{text-align:right;white-space:nowrap}
      .tot td{font-weight:700;background:#f9f5f6;border-top:2px solid #800020} .none{text-align:center;color:#888;font-style:italic}
      @page{size:A4 landscape;margin:10mm} @media print{body{margin:0}}
    </style></head><body>
      <div class="co"><h1>${esc(c.companyName || '')}</h1>${addr ? `<div>${esc(addr)}</div>` : ''}${contact ? `<div>${contact}</div>` : ''}
        <h2>Withholding Tax Detail</h2><div class="per">${esc(periodText)}${branchName ? ` &nbsp;·&nbsp; ${esc(branchName)}` : ''}</div></div>
      ${body}
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <>
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={fyId} onChange={(e) => setFyId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map((fy) => <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup><Label>From Date</Label><DateInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></FilterGroup>
          <FilterGroup><Label>To Date</Label><DateInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></FilterGroup>
          <FilterGroup style={{ minWidth: 240 }}>
            <Label>Search</Label>
            <SearchBox><FiSearch /><input value={search} placeholder="Party, voucher, invoice, NTN…" onChange={(e) => setSearch(e.target.value)} /></SearchBox>
          </FilterGroup>
        </FiltersRow>
        <ButtonRow>
          <Btn type="button" onClick={handleExcel} disabled={!generated || loading || !hasRows}><FiDownload /> Excel</Btn>
          <Btn type="button" onClick={handlePrint} disabled={!generated || loading || !hasRows}><FiPrinter /> Print</Btn>
          <PrimaryBtn type="button" onClick={generate} disabled={loading}><FiBarChart2 /> {loading ? 'Generating...' : 'Generate Report'}</PrimaryBtn>
        </ButtonRow>
      </FiltersCard>

      {generated && !loading && (
        <Cards>
          <Card><span>WHT Deducted on Payments</span><strong>{fmtAmt(totals.payment.wht)}</strong><small>Payable to FBR</small></Card>
          <Card><span>WHT Deducted by Customers</span><strong>{fmtAmt(totals.receipt.wht)}</strong><small>Claimable</small></Card>
          <Card><span>{advanceLabel} Collected</span><strong>{fmtAmt(totals.advance.wht)}</strong><small>Payable to FBR</small></Card>
        </Cards>
      )}

      <Content>
        {!generated ? (
          <Empty>Select the period and press <strong>Generate Report</strong>.</Empty>
        ) : loading ? (
          <Empty>Loading…</Empty>
        ) : (
          <>
            <Title>
              <h3>Withholding Tax Detail</h3>
              <p>{activeCompany?.companyName || ''} | {periodText}{branchName ? ` | ${branchName}` : ''}</p>
            </Title>
            {sections.map((s) => (
              <Section key={s.key}>
                <SectionHead>{s.title} <em>({shown[s.key].length})</em></SectionHead>
                <Scroll>
                  <Table>
                    <thead><tr>{COLS.map((h, i) => <th key={h} className={i >= 8 ? 'r' : ''}>{h}</th>)}</tr></thead>
                    <tbody>
                      {shown[s.key].length === 0 ? (
                        <tr><td colSpan={11} className="none">No entries in this period</td></tr>
                      ) : shown[s.key].map((r, i) => (
                        <tr key={`${r.transId}-${i}`}>
                          <td>{fmtD(r.date)}</td>
                          <td><DocLink type="button" onClick={() => navigateToTransaction(navigate, r.transType, r.transId)}>{r.transNo}</DocLink></td>
                          <td>{r.refNo}</td>
                          <td>{fmtD(r.refDate)}</td>
                          <td>{r.billNo}</td>
                          <td>{r.party}</td>
                          <td>{r.ntn}</td>
                          <td>{r.tax}</td>
                          <td className="r">{fmtAmt(r.taxable)}</td>
                          <td className="r">{fmtRate(r.rate)}</td>
                          <td className="r"><b>{fmtAmt(r.wht)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="tot">
                        <td colSpan={8} className="r">Total</td>
                        <td className="r">{fmtAmt(totals[s.key].taxable)}</td>
                        <td />
                        <td className="r">{fmtAmt(totals[s.key].wht)}</td>
                      </tr>
                    </tfoot>
                  </Table>
                </Scroll>
              </Section>
            ))}
          </>
        )}
      </Content>
    </>
  );
};

export default WhtStatement;

const FiltersCard = styled.div`background: white; border-radius: 12px; padding: 18px 20px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.08);`;
const FiltersRow = styled.div`display: flex; gap: 14px; flex-wrap: wrap; align-items: flex-end;`;
const FilterGroup = styled.div`display: flex; flex-direction: column; gap: 6px; min-width: 160px;`;
const Label = styled.label`font-size: 12px; font-weight: 600; color: ${colors.text.secondary};`;
const Select = styled.select`padding: 9px 10px; border: 1px solid ${colors.border?.light || '#ddd'}; border-radius: 8px; font-size: 14px; background: white;`;
const DateInput = styled.input`padding: 8px 10px; border: 1px solid ${colors.border?.light || '#ddd'}; border-radius: 8px; font-size: 14px;`;
const SearchBox = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 0 10px; border: 1px solid ${colors.border?.light || '#ddd'}; border-radius: 8px; background: white;
  input { border: none; outline: none; padding: 9px 0; font-size: 14px; flex: 1; }
  svg { color: #888; }
`;
const ButtonRow = styled.div`display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; flex-wrap: wrap;`;
const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13px;
  border: 1px solid ${colors.primary.main}; color: ${colors.primary.main}; background: white;
  &:disabled { opacity: 0.5; cursor: default; }
`;
const PrimaryBtn = styled(Btn)`background: ${colors.primary.main}; color: white;`;
const Cards = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; margin-bottom: 16px;`;
const Card = styled.div`
  background: white; border-radius: 12px; padding: 14px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); display: flex; flex-direction: column; gap: 4px;
  span { font-size: 12px; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
  strong { font-size: 22px; color: ${colors.primary.main}; font-family: monospace; }
  small { font-size: 11px; color: #888; }
`;
const Content = styled.div`background: white; border-radius: 12px; padding: 18px 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.08);`;
const Empty = styled.div`padding: 50px 20px; text-align: center; color: ${colors.text.secondary};`;
const Title = styled.div`
  text-align: center; margin-bottom: 8px;
  h3 { margin: 0; font-size: 18px; color: ${colors.primary.main}; }
  p { margin: 4px 0 0; font-size: 12px; color: ${colors.text.secondary}; }
`;
const Section = styled.div`margin-top: 18px;`;
const SectionHead = styled.div`
  font-size: 14px; font-weight: 700; color: ${colors.primary.main}; padding-bottom: 6px; margin-bottom: 6px; border-bottom: 2px solid ${colors.primary.main};
  em { font-style: normal; font-weight: 400; color: #888; font-size: 12px; }
`;
const Scroll = styled.div`overflow-x: auto;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12.5px;
  th { background: #f5f5f5; text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; color: #555; border-bottom: 1px solid #ddd; white-space: nowrap; }
  td { padding: 7px 8px; border-bottom: 1px solid #eee; }
  .r { text-align: right; white-space: nowrap; font-family: 'Consolas', monospace; }
  th.r { font-family: inherit; }
  .none { text-align: center; color: #999; font-style: italic; padding: 14px; }
  .tot td { font-weight: 700; background: #faf5f6; border-top: 2px solid ${colors.primary.main}; }
`;
const DocLink = styled.button`
  background: none; border: none; padding: 0; color: ${colors.primary.main}; font-weight: 600; cursor: pointer; font-size: inherit;
  &:hover { text-decoration: underline; }
`;
